import axios from "axios";
import { getToken, logout, isTokenExpired } from "../auth/auth";

const API = axios.create({
  baseURL: "https://api.technnext.com/api",
});

// Attach the JWT to every request — but only if it's actually present and well-formed
API.interceptors.request.use((config) => {
  const token = getToken();
  // a real JWT has two dots; skip anything blank, "undefined", or malformed
  if (token && token.split(".").length === 3) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Only force logout when the session is genuinely invalid/expired —
// not for every 401/403 (an admin-only endpoint returning 403 shouldn't log you out)
API.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const errorCode = err.response?.data?.errorCode;
    // Log out on a genuine 401. Two ways we can tell it's genuine:
    //  1. The backend told us directly via errorCode (AUTH_REQUIRED /
    //     AUTH_TOKEN_EXPIRED / AUTH_TOKEN_INVALID - see
    //     GlobalExceptionHandler / ErrorResponseWriter / JwtFilter). This is
    //     the reliable signal, since the server actually validated the
    //     token's signature.
    //  2. Fallback to the old client-side check (missing/locally-expired
    //     token) for safety, in case an older backend build doesn't send
    //     errorCode yet.
    // Root cause this fixes: isTokenExpired() alone only catches a token
    // whose exp claim has passed - it can't detect a token that's invalid
    // for any other reason (e.g. signed with an old/rotated secret). Such a
    // token was never being cleared from storage, so it kept getting
    // reattached to every request (including the 30s notification poll),
    // producing the same 401 over and over instead of the app recovering.
    const isAuthFailure =
      typeof errorCode === "string" && errorCode.startsWith("AUTH_");
    if (status === 401 && (isAuthFailure || !getToken() || isTokenExpired())) {
      logout();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

// Reads the backend's error envelope ({ success, status, errorCode, message,
// timestamp } — see ApiError.java / GlobalExceptionHandler) and turns it
// into ONE correct, user-facing string, without guessing based on HTTP
// status alone. The backend now guarantees status accurately reflects the
// real cause (401 = genuine auth failure, 403 = genuine permission denial,
// 409/400 = business/validation error, 404 = not found, 5xx = real server
// error) and always sends a real `message` — so this is a thin, consistent
// wrapper, not a place to re-guess what went wrong.
//
// Usage: catch (err) { toast.error(getErrorMessage(err)); }
// An optional second arg overrides the final network/unknown-error fallback
// text for pages that want something more specific.
export function getErrorMessage(err, fallback) {
  const res = err?.response;
  const data = res?.data;
  const backendMessage = data?.message;

  // No response at all: the request never reached the server (offline, CORS,
  // server down, DNS, etc.) — this is NOT a 401/403/business error, so don't
  // present it as one.
  if (!res) {
    return (
      fallback ||
      "Cannot reach the server — check your connection and try again."
    );
  }

  switch (res.status) {
    case 401:
      return backendMessage || "Your session has expired. Please log in again.";
    case 403:
      return (
        backendMessage || "You do not have permission to perform this action."
      );
    case 404:
      return backendMessage || "The requested item was not found.";
    case 409:
      return backendMessage || "This conflicts with existing data.";
    case 400:
      return backendMessage || "The submitted data is invalid.";
    case 413:
      return backendMessage || "The file is too large.";
    case 415:
      return backendMessage || "That file type isn't supported.";
    default:
      if (res.status >= 500) {
        return (
          backendMessage ||
          "Something went wrong on the server. Please try again."
        );
      }
      return backendMessage || fallback || `Request failed (${res.status})`;
  }
}

export default API;
